import Payment from 'app/src/payments/Payment';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import CCCharge from 'app/src/data/models/CCCharge';
import SalePayment from 'app/src/data/models/SalePayment';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import PaymentTransactionCollection from 'app/src/data/models/PaymentTransactionCollection';
import Format from 'app/src/data/Format';
import _ from 'underscore';
import $ from 'jquery';
import Decimal from '../data/base/Decimal';

/**
 * Class definition for a terminal payment.
 */
export default class TerminalPayment extends Payment {
  /**
   * Constructor
   *
   * @param amount {Decimal}   The amount of the terminal payment
   * @param ccChargeID {Number}   The CCCharge ID associated with the terminal transaction
   * @param rawResponse {String}  The raw response from the server from a transaction
   * @param saleID {Number}   The ID of the sale for the payment
   */
  constructor(
    amount = new Decimal(0),
    ccChargeID = 0,
    rawResponse = null,
    saleID = 0,
    tipAmount = null
  ) {
    super(
      amount,
      PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.TERMINAL),
      rawResponse,
      saleID,
      tipAmount
    );

    this._model.set('ccChargeID', ccChargeID);

    this.rawResponse = rawResponse;
    this.paymentTransaction = null;
  }

  /**
   * Getter for the internally maintained raw response from the terminal for the payment.
   *
   * @returns {Object|null}
   */
  get rawResponse() {
    return this._rawResponse;
  }

  /**
   * Setter for the internally maintained raw response from the terminal for the payment.
   *
   * @param rawResponse {Object|null}  The raw response from the terminal.
   */
  set rawResponse(rawResponse) {
    if (!(rawResponse instanceof PaymentTransaction) && rawResponse) {
      if (_.isObject(rawResponse)) {
        this._rawResponse = btoa(JSON.stringify(rawResponse));
      } else {
        this._rawResponse = btoa(rawResponse);
      }
    } else {
      this._rawResponse = rawResponse;
    }
  }

  /**
   * Retrieve the payment service payment transaction associated with this payment.  Once CCCharge is no longer
   * utilized for payments this method will be deprecated as the model for the payment
   * will then be a Payment Service payment.
   *
   * @returns {PaymentTransaction}   The Payment Service Payment Transaction model
   */
  get paymentTransaction() {
    return this._paymentTransaction;
  }

  /**
   * Sets the payment service payment transaction associated with the payment.  Once CCCharge is no longer
   * utilized for payments this method will be deprecated as the model for the payment
   * will then be a Payment Service payment (which will include the transaction information).
   *
   * @param paymentTransaction {PaymentTransaction}  The Payment Service Payment Transaction model
   */
  set paymentTransaction(paymentTransaction) {
    this._paymentTransaction = paymentTransaction;
  }

  /**
   * Retrieves the details of the payment.  Useful for abstracting payment
   * terminal information irrespective of whether the data is coming from
   * a CCCharge or a Payment Service payment.  As more payments are migrated
   * to the Payment Service it should no longer be necessary to support the
   * CCCharge implementation.
   *
   * @returns {{}}    An object of the form:
   *                  {
   *                      cardType : String => The card type of the payment
   *                      xnum: String => The last four digits of the card number
   *                  }
   */
  get details() {
    let details = {};

    if (this._model.has('CCCharge')) {
      details.cardBrand = this._model.get('CCCharge').cardBrand;
      details.cardType = this._model.get('CCCharge').cardType;
      details.maskedDigits = this._model.get('CCCharge').xnum;
      details.isDebit = Format.parseBoolean(this._model.get('CCCharge').isDebit);
      details.transactionDate = Format.parseDate(this._model.get('CCCharge').timeStamp);
    } else {
      const paymentTransaction = new PaymentTransactionCollection(
        this.serviceModel.get('Transactions'),
        {
          parse: true,
        }
      ).first();

      if (paymentTransaction) {
        details.cardBrand = paymentTransaction.cardBrand;
        details.cardType = paymentTransaction.cardType;
        details.maskedDigits = paymentTransaction.cardNumber.slice(-4);
        details.isDebit = paymentTransaction.isDebit();
        details.isInterac =
          paymentTransaction.cardType === PaymentTransaction.SupportedCardType.INTERAC;
        details.isBancontact =
          paymentTransaction.cardType === PaymentTransaction.SupportedCardType.BANCONTACT;
        details.isIdeal =
          paymentTransaction.cardType === PaymentTransaction.SupportedCardType.IDEAL;
        details.transactionDate = Format.parseDate(paymentTransaction.get('createdAt'));
      }
    }

    return details;
  }

  /**
   * A terminal payment expects the raw terminal response to be present when committing a
   * payment result.  The method moving forward utilizes the PaymentTransaction endpoint; however,
   * Cayan still utilizes the older method of using a PUT against the CCCharge so both methods
   * must be supported for now.
   *
   * @returns {jQuery.Promise}    Resolves with a SalePayment model
   */
  commit() {
    let deferred = $.Deferred();
    if (this._rawResponse) {
      if (this._rawResponse instanceof PaymentTransaction) {
        // This is a SalePayment backed by the payment service

        this._savePaymentTransaction()
          .done(() => {
            deferred.resolve(this._model);
          })
          .fail(() => {
            deferred.reject();
          });
      } else if (this._model.get('ccChargeID') > 0) {
        const tipAmount = this._model.get('tipAmount') || null;
        // Commit the raw response via the CCCharge (Cayan only).
        // This will be deprecated once the Cayan implementation is migrated
        // to the payment service
        let ccCharge = new CCCharge({
          ccChargeID: this._model.get('ccChargeID'),
          terminalResponse: this._rawResponse,
          SalePayments: {
            SalePayment: {
              ...(tipAmount && { tipAmount }),
            },
          },
        });

        ccCharge
          .save()
          .done(response => {
            // update the internal model with the response
            this._model.set(
              new SalePayment(response.CCCharge.SalePayments.SalePayment, {
                parse: true,
              }).attributes
            );
            deferred.resolve(this._model);
          })
          .fail(() => {
            deferred.reject();
          });
      }
    } else {
      deferred.resolve(this._model);
    }

    return deferred.promise();
  }

  /**
   * Conform the JSON API to allow serialization of a TerminalPayment object.  This extends
   * the base implementation to serialize the service payment (when present) and the payment
   * details.
   *
   * @returns {{model: *}}
   */
  toJSON() {
    let paymentData = super.toJSON();

    if (this.paymentTransaction) {
      paymentData.paymentTransaction = this.paymentTransaction.toJSON();
    }

    paymentData.details = this.details;

    return paymentData;
  }

  /**
   * Clones the terminal payment
   *
   * @returns {Payment}   The cloned payment
   */
  clone() {
    let clone = super.clone();

    // copy terminal specific data into the clone
    clone._servicePayment = this.servicePayment;
    clone._rawResponse = this.rawResponse;

    return clone;
  }

  /**
   * Saves the payment transaction data to the server.   If the request fails because
   * of an ID conflict (an extremely unlikely scenario) or the back-end has already retrieved
   * the payment status it will raise a 409 error.
   *
   * @returns {jQuery.promise}    Resolves onces the payment transaction is successfully saved and
   *                              is rejected when the request fails for any reason other than
   *                              an ID conflict.
   * @private
   */
  _savePaymentTransaction() {
    let deferred = $.Deferred();

    this._rawResponse
      .save()
      .done(() => {
        this.paymentTransaction = this._rawResponse;
        deferred.resolve();
      })
      .fail(jqXHR => {
        if (jqXHR.status !== 409) {
          deferred.reject();
        } else {
          // FIXME: We currently have no way to differentiate between
          // a used UUID and the case where the transaction details has already
          // been retrieved by the server.  For now, just resolve the promise
          // because when we refresh the sale (in the sale manager) it will get
          // the payment details.  The case of the used UUID will have to be
          // fixed once the back-end returns us enough information so we can
          // differentiate

          deferred.resolve();
          /*              // regenerate the ID and try again
                this._rawResponse.unset('paymentTransactionID');
                this._rawResponse = new PaymentTransaction(this._rawResponse.attributes);

                // prevent the buffer from overflowing
                setTimeout(() => {
                    deferred = this._savePaymentTransaction();
                }, 0);
*/
        }
      });

    return deferred.promise();
  }
}
